import { useCallback } from "react";
import { useAppSelector } from "@/store/store";
import {
  selectAuthError,
  selectAuthSession,
  selectAuthStatus,
  selectIdentity,
  selectIsAuthenticated,
  selectIsAuthLoading,
  selectRequiresRestaurantSelection,
} from "@/selectors/auth.selectors";
import { authService } from "@/services/auth.service";
import type { LoginRequest } from "@/domain/auth/login-request";
import type { ForgotPasswordRequestDto, ResetPasswordRequestDto, RegisterRequestDto } from "@/domain/auth/auth.api.types";

export function useAuth() {
  const status = useAppSelector(selectAuthStatus);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsAuthLoading);
  const identity = useAppSelector(selectIdentity);
  const error = useAppSelector(selectAuthError);
  const session = useAppSelector(selectAuthSession);
  const requiresRestaurantSelection = useAppSelector(selectRequiresRestaurantSelection);

  const login = useCallback((credentials: LoginRequest) => authService.login(credentials), []);
  const getGoogleAuthUrl = useCallback(
    (redirectUri: string) => authService.getGoogleAuthUrl(redirectUri),
    []
  );
  const getFacebookAuthUrl = useCallback(
    (redirectUri: string) => authService.getFacebookAuthUrl(redirectUri),
    []
  );
  const loginWithGoogleCallback = useCallback(
    (code: string, state: string) => authService.loginWithGoogleCallback(code, state),
    []
  );
  const loginWithOAuthRedirectTokens = useCallback(
    (accessToken: string, refreshToken?: string | null) =>
      authService.loginWithOAuthRedirectTokens(accessToken, refreshToken),
    []
  );
  /** @deprecated Prefer loginWithOAuthRedirectTokens */
  const loginWithGoogleRedirectTokens = useCallback(
    (accessToken: string, refreshToken?: string | null) =>
      authService.loginWithOAuthRedirectTokens(accessToken, refreshToken),
    []
  );
  const requestPasswordReset = useCallback(
    (payload: ForgotPasswordRequestDto) => authService.requestPasswordReset(payload),
    []
  );
  const resetPassword = useCallback(
    (payload: ResetPasswordRequestDto) => authService.resetPassword(payload),
    []
  );

  const register = useCallback((payload: RegisterRequestDto) => authService.register(payload), []);

  const logout = useCallback(() => authService.logout(), []);

  const retryBootstrap = useCallback(() => authService.bootstrap(), []);

  return {
    status,
    isAuthenticated,
    isLoading,
    identity,
    error,
    session,
    requiresRestaurantSelection,
    login,
    getGoogleAuthUrl,
    getFacebookAuthUrl,
    loginWithGoogleCallback,
    loginWithOAuthRedirectTokens,
    loginWithGoogleRedirectTokens,
    requestPasswordReset,
    resetPassword,
    register,
    logout,
    retryBootstrap,
  };
}
